import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Plus, TrendingUp, TrendingDown, Trash2, DollarSign, Banknote, Coins, Calendar, X } from "lucide-react";
import DatafonosPanel from "../components/DatafonosPanel";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import { todayCR } from "../utils/dateHelpers";

export default function Banks({ user }) {
  const [banks, setBanks] = useState([]);
  const [masters, setMasters] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const isSuperMaster = !!user?.is_super_master;
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [showBillsDialog, setShowBillsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [showDeleteTxDialog, setShowDeleteTxDialog] = useState(false);
  const [deleteTxPassword, setDeleteTxPassword] = useState("");
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [filterDate, setFilterDate] = useState("");  // Date filter for transactions
  
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });
  
  const [transactionData, setTransactionData] = useState({
    type: "entrada",
    amount_colones: 0,
    amount_dollars: 0,
    description: "",
    source_bank_id: ""  // Para "entrada_a_caja" - de qué banco sale el dinero
  });
  
  const [billsData, setBillsData] = useState({
    billetes_20000: 0,
    billetes_10000: 0,
    billetes_5000: 0,
    billetes_2000: 0,
    billetes_1000: 0,
    monedas_500: 0,
    monedas_100: 0,
    monedas_50: 0,
    monedas_25: 0,
    monedas_10: 0,
    monedas_5: 0,
    dollars_100: 0,
    dollars_50: 0,
    dollars_20: 0,
    dollars_10: 0,
    dollars_5: 0,
    dollars_1: 0
  });

  useEffect(() => {
    fetchBanks();
    axiosInstance.get("/boxes").then((r) => setBoxes(r.data || [])).catch(() => setBoxes([]));
    if (isSuperMaster) {
      axiosInstance.get("/users").then((r) => setMasters((r.data || []).filter((u) => u.role === "master"))).catch(() => setMasters([]));
    }
  }, [isSuperMaster]);

  const handleAssignOwner = async (bankId, ownerId) => {
    if (!ownerId) return;
    try {
      await axiosInstance.post(`/banks/${bankId}/assign-owner?owner_master_id=${ownerId}`);
      toast.success("Dueño asignado");
      fetchBanks();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al asignar dueño"));
    }
  };

  const handleToggleBoxScope = async (bank, boxId) => {
    // If boxId === null → clear scope (bank visible in ALL boxes of owner)
    let next;
    if (boxId === null) {
      next = [];
    } else {
      const current = bank.allowed_box_ids || [];
      next = current.includes(boxId)
        ? current.filter((b) => b !== boxId)
        : [...current, boxId];
    }
    try {
      const params = new URLSearchParams();
      next.forEach((b) => params.append("allowed_box_ids", b));
      await axiosInstance.patch(`/banks/${bank.id}/scope?${params.toString()}`);
      toast.success(next.length ? `Visible en ${next.length} caja(s)` : "Visible en TODAS las cajas");
      fetchBanks();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al actualizar cajas"));
    }
  };

  const isBankOwner = (bank) => {
    if (!user) return false;
    if (isSuperMaster) return true;
    return bank.owner_master_id === user.id;
  };

  const fetchBanks = async () => {
    try {
      const response = await axiosInstance.get("/banks");
      setBanks(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar bancos"));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBank = async () => {
    if (!formData.name) {
      toast.error("Ingresa un nombre para el banco");
      return;
    }

    try {
      await axiosInstance.post("/banks", formData);
      toast.success("Banco creado exitosamente");
      setShowCreateDialog(false);
      setFormData({ name: "", description: "" });
      fetchBanks();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al crear banco"));
    }
  };

  const handleCreateTransaction = async () => {
    if (!selectedBank) return;
    
    if (transactionData.amount_colones <= 0 && transactionData.amount_dollars <= 0) {
      toast.error("Ingresa al menos un monto");
      return;
    }

    try {
      await axiosInstance.post("/banks/transactions", {
        bank_id: selectedBank.id,
        ...transactionData
      });
      
      toast.success("Movimiento registrado exitosamente");
      setShowTransactionDialog(false);
      setTransactionData({
        type: "entrada",
        amount_colones: 0,
        amount_dollars: 0,
        description: "",
        source_bank_id: ""
      });
      fetchBanks();
      if (selectedBank) {
        loadBankDetails(selectedBank.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar movimiento"));
    }
  };

  const handleUpdateBills = async () => {
    if (!selectedBank) return;

    try {
      await axiosInstance.patch(`/banks/${selectedBank.id}/bills`, billsData);
      toast.success("Billetes y monedas actualizados");
      setShowBillsDialog(false);
      fetchBanks();
      loadBankDetails(selectedBank.id);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al actualizar billetes"));
    }
  };

  const loadBankDetails = async (bankId) => {
    try {
      const response = await axiosInstance.get(`/banks/${bankId}`);
      setSelectedBank(response.data);
      
      // Load bills data
      setBillsData({
        billetes_20000: response.data.billetes_20000 || 0,
        billetes_10000: response.data.billetes_10000 || 0,
        billetes_5000: response.data.billetes_5000 || 0,
        billetes_2000: response.data.billetes_2000 || 0,
        billetes_1000: response.data.billetes_1000 || 0,
        monedas_500: response.data.monedas_500 || 0,
        monedas_100: response.data.monedas_100 || 0,
        monedas_50: response.data.monedas_50 || 0,
        monedas_25: response.data.monedas_25 || 0,
        monedas_10: response.data.monedas_10 || 0,
        monedas_5: response.data.monedas_5 || 0,
        dollars_100: response.data.dollars_100 || 0,
        dollars_50: response.data.dollars_50 || 0,
        dollars_20: response.data.dollars_20 || 0,
        dollars_10: response.data.dollars_10 || 0,
        dollars_5: response.data.dollars_5 || 0,
        dollars_1: response.data.dollars_1 || 0
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar detalles"));
    }
  };

  const handleDeleteBank = async () => {
    if (!selectedBank) return;

    try {
      await axiosInstance.delete(`/banks/${selectedBank.id}?password=${deletePassword}`);
      toast.success("Banco eliminado exitosamente");
      setShowDeleteDialog(false);
      setDeletePassword("");
      setSelectedBank(null);
      fetchBanks();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al eliminar banco"));
    }
  };

  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return;

    try {
      await axiosInstance.delete(`/banks/transactions/${transactionToDelete.id}`, {
        data: { password: deleteTxPassword }
      });
      toast.success("Transacción eliminada exitosamente");
      setShowDeleteTxDialog(false);
      setDeleteTxPassword("");
      setTransactionToDelete(null);
      fetchBanks();
      if (selectedBank) {
        loadBankDetails(selectedBank.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al eliminar transacción"));
    }
  };

  const openDeleteTxDialog = (tx) => {
    setTransactionToDelete(tx);
    setDeleteTxPassword("");
    setShowDeleteTxDialog(true);
  };

  const formatCurrency = (amount, currency = "colones") => {
    if (currency === "dollars") {
      return `$${amount.toFixed(2)}`;
    }
    return `₡${amount.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (user?.role !== "master") {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-slate-600">No tienes permiso para acceder a esta sección</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Bancos (Bóvedas)</h1>
          <p className="text-sm text-slate-600">Gestiona depósitos y efectivo en bancos</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Banco
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Banks List */}
        <div className="lg:col-span-1 space-y-3">
          {banks.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-slate-600">No hay bancos creados</p>
              </CardContent>
            </Card>
          ) : (
            banks.map(bank => {
              // Calcular si este banco recibió pagos por datáfono HOY
              const _today = todayCR();
              let _cardToday = 0;
              (bank.transactions || []).forEach((t) => {
                if (t.datafono_id && (t.created_at || "").slice(0, 10) === _today) {
                  _cardToday += Number(t.amount_colones || 0);
                }
              });
              const _hasCardToday = _cardToday > 0;
              return (
              <Card 
                key={bank.id}
                className={`cursor-pointer transition-all ${selectedBank?.id === bank.id ? 'ring-2 ring-blue-500' : 'hover:shadow-md'} ${_hasCardToday ? 'border-emerald-400 ring-1 ring-emerald-200 bg-emerald-50/40' : ''}`}
                onClick={() => loadBankDetails(bank.id)}
                data-testid={`bank-card-${bank.id}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {bank.name}
                    {_hasCardToday && (
                      <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-white bg-emerald-600 px-1.5 py-0.5 rounded-full" title={`Recibió ₡${_cardToday.toLocaleString('es-CR')} por tarjeta hoy`} data-testid={`bank-card-badge-${bank.id}`}>
                        💳 ₡{_cardToday.toLocaleString('es-CR', { maximumFractionDigits: 0 })}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="space-y-1">
                    <p className="text-base font-bold text-green-700">{formatCurrency(bank.total_colones)}</p>
                    <p className="text-xs font-semibold text-green-600">{formatCurrency(bank.total_dollars, "dollars")}</p>
                    {bank.description && (
                      <p className="text-xs text-slate-500 mt-1">{bank.description}</p>
                    )}
                    {isSuperMaster && (
                      <div className="mt-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                        <p className="text-[10px] text-slate-500 mb-0.5">
                          Dueño: <b>{bank.owner_master_id ? (masters.find(m => m.id === bank.owner_master_id)?.username || bank.owner_master_id.slice(0, 8)) : <span className="text-amber-600">Sin asignar</span>}</b>
                        </p>
                        <Select
                          value={bank.owner_master_id || ""}
                          onValueChange={(v) => handleAssignOwner(bank.id, v)}
                        >
                          <SelectTrigger className="h-7 text-xs" data-testid={`bank-assign-${bank.id}`}>
                            <SelectValue placeholder="Asignar dueño…" />
                          </SelectTrigger>
                          <SelectContent>
                            {masters.map((m) => (
                              <SelectItem key={m.id} value={m.id}>{m.username}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {isBankOwner(bank) && bank.allowed_box_ids && bank.allowed_box_ids.length > 0 && (
                      <div className="mt-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                        <p className="text-[10px] text-slate-500">
                          Este banco tenía scope por caja. <button className="underline text-blue-600" onClick={() => handleToggleBoxScope(bank, null)}>Limpiar (visible en todas)</button>
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              );
            })
          )}
        </div>

        {/* Bank Details */}
        <div className="lg:col-span-2">
          {selectedBank ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    {selectedBank.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowBillsDialog(true)}
                    >
                      <Coins className="w-4 h-4 mr-2" />
                      Billetes/Monedas
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTransactionDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Movimiento
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="summary" className="w-full">
                  <TabsList>
                    <TabsTrigger value="summary">Resumen</TabsTrigger>
                    <TabsTrigger value="transactions">Movimientos</TabsTrigger>
                    <TabsTrigger value="datafonos" data-testid="tab-datafonos">Datáfonos</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="summary" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="bg-green-50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-slate-600 mb-1">Total Colones</p>
                          <p className="text-xl font-bold text-green-700">{formatCurrency(selectedBank.total_colones)}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-slate-600 mb-1">Total Dólares</p>
                          <p className="text-xl font-bold text-blue-700">{formatCurrency(selectedBank.total_dollars, "dollars")}</p>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="transactions">
                    {/* Date Filter */}
                    <div className="flex items-center gap-2 mb-4 p-3 bg-slate-100 rounded-lg">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      <Label className="text-sm text-slate-600">Filtrar por fecha:</Label>
                      <Input
                        type="date"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        className="w-40 h-8 text-sm"
                      />
                      {filterDate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => setFilterDate("")}
                        >
                          <X className="w-4 h-4" />
                          Limpiar
                        </Button>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      {selectedBank.transactions && selectedBank.transactions.length > 0 ? (
                        (() => {
                          // Filter transactions by date if filter is set
                          const filteredTxs = filterDate 
                            ? selectedBank.transactions.filter(tx => {
                                if (!tx.created_at) return false;
                                const txDate = tx.created_at.split('T')[0];
                                return txDate === filterDate;
                              })
                            : selectedBank.transactions;
                          
                          if (filteredTxs.length === 0) {
                            return (
                              <p className="text-center text-slate-500 py-6">
                                {filterDate ? `No hay movimientos para ${filterDate}` : "No hay movimientos"}
                              </p>
                            );
                          }
                          
                          return filteredTxs.map(tx => (
                          <Card key={tx.id} className="bg-slate-50">
                            <CardContent className="pt-3 pb-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {tx.type === "entrada" || tx.type === "deposito" ? (
                                    <TrendingUp className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <TrendingDown className="w-4 h-4 text-red-600" />
                                  )}
                                  <div>
                                    <p className="text-xs font-semibold">{tx.description}</p>
                                    <p className="text-xs text-slate-500">
                                      {tx.type === "deposito" ? "Depósito" : tx.type === "entrada" ? "Entrada" : "Salida"}
                                      {tx.box_name && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">📦 {tx.box_name}</span>}
                                    </p>
                                    {tx.created_at && (
                                      <p className="text-xs text-slate-400 mt-1">
                                        📅 {new Date(tx.created_at).toLocaleDateString('es-CR', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric' 
                                        })} - {new Date(tx.created_at).toLocaleTimeString('es-CR', { 
                                          hour: '2-digit', 
                                          minute: '2-digit' 
                                        })}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="text-right">
                                    <p className="text-xs font-bold">{formatCurrency(tx.amount_colones)}</p>
                                    <p className="text-xs">{formatCurrency(tx.amount_dollars, "dollars")}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDeleteTxDialog(tx);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ));
                        })()
                      ) : (
                        <p className="text-center text-slate-500 py-6">No hay movimientos</p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="datafonos">
                    <DatafonosPanel
                      bankId={selectedBank.id}
                      bankName={selectedBank.name}
                      canEdit={user?.role === 'master'}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-slate-600">Selecciona un banco para ver detalles</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Bank Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nuevo Banco</DialogTitle>
            <DialogDescription>Ingresa los datos del banco (bóveda)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Bóveda Principal"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción opcional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateBank}>Crear Banco</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction Dialog */}
      <Dialog open={showTransactionDialog} onOpenChange={setShowTransactionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Movimiento</DialogTitle>
            <DialogDescription>Registra entrada o salida de dinero</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Movimiento *</Label>
              <Select 
                value={transactionData.type} 
                onValueChange={(val) => setTransactionData({ ...transactionData, type: val, source_bank_id: "" })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrada">Entrada (Depósito al banco)</SelectItem>
                  <SelectItem value="salida">Salida (Retiro del banco)</SelectItem>
                  <SelectItem value="entrada_a_caja">Entrada a Caja (Sale de Bóveda)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {transactionData.type === "entrada" && "Dinero que entra al banco (ej: cliente deposita)"}
                {transactionData.type === "salida" && "Dinero que sale del banco"}
                {transactionData.type === "entrada_a_caja" && "Dinero que sale de una bóveda y ENTRA A LA CAJA"}
              </p>
            </div>
            
            {/* Selector de banco fuente para "entrada_a_caja" */}
            {transactionData.type === "entrada_a_caja" && (
              <div>
                <Label>Banco/Bóveda de Origen *</Label>
                <Select 
                  value={transactionData.source_bank_id} 
                  onValueChange={(val) => setTransactionData({ ...transactionData, source_bank_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el banco de origen" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map(bank => (
                      <SelectItem key={bank.id} value={bank.id}>
                        {bank.name} (₡{bank.total_colones?.toLocaleString() || 0})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-orange-600 mt-1">
                  ⚠️ El dinero SALDRÁ de esta bóveda y ENTRARÁ a la caja registradora
                </p>
              </div>
            )}
            
            <div>
              <Label>Monto Colones (₡)</Label>
              <Input
                type="number"
                step="0.01"
                value={transactionData.amount_colones}
                onChange={(e) => setTransactionData({ ...transactionData, amount_colones: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Monto Dólares ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={transactionData.amount_dollars}
                onChange={(e) => setTransactionData({ ...transactionData, amount_dollars: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Descripción *</Label>
              <Input
                value={transactionData.description}
                onChange={(e) => setTransactionData({ ...transactionData, description: e.target.value })}
                placeholder="Descripción del movimiento"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransactionDialog(false)}>Cancelar</Button>
            <Button 
              onClick={handleCreateTransaction}
              disabled={transactionData.type === "entrada_a_caja" && !transactionData.source_bank_id}
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bills Dialog */}
      <Dialog open={showBillsDialog} onOpenChange={setShowBillsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Billetes y Monedas</DialogTitle>
            <DialogDescription>Actualiza el conteo físico de billetes y monedas</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Colones</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "₡20,000", field: "billetes_20000" },
                  { label: "₡10,000", field: "billetes_10000" },
                  { label: "₡5,000", field: "billetes_5000" },
                  { label: "₡2,000", field: "billetes_2000" },
                  { label: "₡1,000", field: "billetes_1000" },
                  { label: "₡500", field: "monedas_500" },
                  { label: "₡100", field: "monedas_100" },
                  { label: "₡50", field: "monedas_50" },
                  { label: "₡25", field: "monedas_25" },
                  { label: "₡10", field: "monedas_10" },
                  { label: "₡5", field: "monedas_5" }
                ].map(item => (
                  <div key={item.field}>
                    <Label>{item.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={billsData[item.field]}
                      onChange={(e) => setBillsData({ ...billsData, [item.field]: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Dólares</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "$100", field: "dollars_100" },
                  { label: "$50", field: "dollars_50" },
                  { label: "$20", field: "dollars_20" },
                  { label: "$10", field: "dollars_10" },
                  { label: "$5", field: "dollars_5" },
                  { label: "$1", field: "dollars_1" }
                ].map(item => (
                  <div key={item.field}>
                    <Label>{item.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={billsData[item.field]}
                      onChange={(e) => setBillsData({ ...billsData, [item.field]: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBillsDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdateBills}>Actualizar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Banco</DialogTitle>
            <DialogDescription>Esta acción no se puede deshacer. El banco debe estar vacío.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Contraseña Master *</Label>
            <Input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Ingresa contraseña"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteBank}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Transaction Dialog */}
      <Dialog open={showDeleteTxDialog} onOpenChange={setShowDeleteTxDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Transacción de Banco</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. El monto será revertido del banco.
              {transactionToDelete && (
                <div className="mt-2 p-2 bg-slate-100 rounded text-sm">
                  <p><strong>Descripción:</strong> {transactionToDelete.description}</p>
                  <p><strong>Monto:</strong> {formatCurrency(transactionToDelete.amount_colones)}</p>
                  <p><strong>Tipo:</strong> {transactionToDelete.type === "entrada" ? "Entrada" : transactionToDelete.type === "deposito" ? "Depósito" : "Salida"}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Clave de Eliminación *</Label>
            <Input
              type="password"
              value={deleteTxPassword}
              onChange={(e) => setDeleteTxPassword(e.target.value)}
              placeholder="Ingresa clave 4981"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteTxDialog(false);
              setDeleteTxPassword("");
              setTransactionToDelete(null);
            }}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteTransaction}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
